import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  AlertTriangle, Bell, Building2, Crown, ChevronDown, ChevronsLeft, ChevronsRight, FilePlus2, KeyRound, Lock, LogOut, Menu, Moon, Package, Plus, Receipt,
  Search, ShoppingCart, Store, Sun, UserPlus, X, LifeBuoy,
} from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { PLAN_FEATURES, ROLE_LABELS, type Permission, type Role } from '@makila/shared';
import { get, post } from '../lib/api';
import { featureOf, useAuth } from '../lib/auth';
import { MODULES, type Module, moduleFor, type NavCounts, type NavGroup, type NavSection } from '../lib/modules';
import { CommandPalette } from './command-palette';
import { Button, Dropdown, Spinner, errorText, useToast } from './ui';
import { MakilaMark } from './brand';
import { AnnouncementsBanner } from '../pages/platform';

export function useTheme() {
  const [dark, setDark] = useState(() => {
    try { const s = localStorage.getItem('mk-theme'); if (s) return s === 'dark'; } catch { /* sem storage */ }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  useEffect(() => {
    document.documentElement.classList.toggle('dark', dark);
    try { localStorage.setItem('mk-theme', dark ? 'dark' : 'light'); } catch { /* sem storage */ }
  }, [dark]);
  return [dark, setDark] as const;
}

const FILTER_KEYS = ['tipo', 'r', 'tab'];
/** Activo quando o caminho e os filtros de navegação (tipo, r, tab) coincidem. */
function isActive(to: string, loc: { pathname: string; search: string }) {
  const [path, qs = ''] = to.split('?');
  if (path !== loc.pathname) return false;
  const want = new URLSearchParams(qs);
  const have = new URLSearchParams(loc.search);
  return FILTER_KEYS.every((k) => (want.get(k) ?? '') === (have.get(k) ?? ''));
}

function Rail({ current, onNavigate }: { current: Module; onNavigate?: () => void }) {
  const { can, session, hostKind } = useAuth();
  return (
    <nav aria-label="Módulos" className="flex h-full w-[76px] flex-col items-center gap-1 bg-[var(--rail)] py-3">
      <Link to="/" onClick={onNavigate} className="mb-3 rounded-xl p-1 ring-1 ring-white/10" aria-label="Makila — início">
        <MakilaMark className="size-9" />
      </Link>
      {MODULES.filter((m) => (hostKind === 'admin' ? (m.platform && !!session?.user.is_platform_admin) : (m.platform ? false : can(m.perm)))).map((m) => {
        const active = m.id === current.id;
        return (
          <Link key={m.id} to={m.home} onClick={onNavigate} aria-current={active ? 'page' : undefined}
            className={clsx('group flex w-[64px] flex-col items-center gap-1 rounded-xl py-2 text-[10.5px] font-medium transition',
              active ? 'bg-white/12 text-white' : 'text-white/55 hover:bg-white/6 hover:text-white')}>
            <span className={clsx('rounded-lg p-1.5 transition', active && 'bg-brand-500 text-white shadow-lg shadow-brand-500/30')}><m.icon className="size-[18px]" /></span>
            {m.label}
          </Link>
        );
      })}
    </nav>
  );
}

function NavItem({ item, counts, onNavigate, nested }: { item: NavSection; counts?: NavCounts; onNavigate?: () => void; nested?: boolean }) {
  const loc = useLocation();
  const active = isActive(item.to, loc);
  const n = counts ? item.count?.(counts) : undefined;
  return (
    <Link to={item.to} onClick={onNavigate} aria-current={active ? 'page' : undefined}
      className={clsx('group relative flex items-center justify-between gap-2 rounded-lg py-1.5 pr-2.5 text-[13.5px] transition-colors',
        nested ? 'pl-9' : 'pl-2.5',
        active ? 'bg-brand-500/10 font-semibold text-brand-800 dark:text-brand-100' : 'text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]')}>
      {nested && <span className={clsx('absolute top-1/2 left-[18px] h-5 w-0.5 -translate-y-1/2 rounded-full transition-colors', active ? 'bg-brand-600' : 'bg-[var(--border-strong)] group-hover:bg-[var(--muted)]')} />}
      <span className="truncate">{item.label}</span>
      {!!n && <CountBadge n={n} warn={item.tone === 'warn'} />}
    </Link>
  );
}

function CountBadge({ n, warn }: { n: number; warn?: boolean }) {
  return <span className={clsx('rounded-full px-1.5 py-px text-[11px] font-semibold tabular-nums', warn ? 'bg-red-500/12 text-red-700 dark:text-red-300' : 'bg-amber-500/15 text-amber-800 dark:text-amber-300')}>{n}</span>;
}

/** Estado aberto/fechado das categorias, lembrado por módulo (só conveniência local). */
function useOpenCategories(moduleId: string) {
  const key = `mk-nav-${moduleId}`;
  const [state, setState] = useState<Record<string, boolean>>(() => { try { return JSON.parse(localStorage.getItem(key) ?? '{}'); } catch { return {}; } });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(state)); } catch { /* sem storage */ } }, [key, state]);
  return [state, setState] as const;
}

/**
 * Categoria recolhível do painel: cabeçalho com ícone, seta que roda e
 * abertura suave (grid 0fr→1fr, sem medir alturas). Abre sozinha quando
 * contém a página actual; fechada, mostra o total dos contadores.
 */
function NavCategory({ moduleId, group, items, counts, onNavigate }: { moduleId: string; group: NavGroup; items: NavSection[]; counts?: NavCounts; onNavigate?: () => void }) {
  const loc = useLocation();
  const [openMap, setOpenMap] = useOpenCategories(moduleId);
  const hasActive = items.some((i) => isActive(i.to, loc));
  const open = openMap[group.title!] ?? true;
  useEffect(() => { if (hasActive && openMap[group.title!] === false) setOpenMap((m) => ({ ...m, [group.title!]: true })); }, [hasActive]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = counts ? items.reduce((s, i) => s + (i.count?.(counts) ?? 0), 0) : 0;
  const warn = items.some((i) => i.tone === 'warn' && counts && (i.count?.(counts) ?? 0) > 0);
  const Icon = group.icon;
  const id = `nav-${moduleId}-${group.title!.replace(/\W+/g, '-')}`;
  return (
    <div className="mt-1">
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpenMap((m) => ({ ...m, [group.title!]: !open }))}
        className={clsx('group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] font-semibold transition-colors',
          hasActive ? 'text-[var(--text)]' : 'text-[var(--text-2)]', 'hover:bg-[var(--surface-2)]')}>
        {Icon && <span className={clsx('grid size-6 place-items-center rounded-md transition-colors', hasActive ? 'bg-brand-600 text-white shadow-sm shadow-brand-600/30' : 'bg-[var(--surface-2)] text-[var(--muted)] group-hover:text-[var(--text)]')}><Icon className="size-3.5" /></span>}
        <span className="flex-1 truncate">{group.title}</span>
        {!open && total > 0 && <CountBadge n={total} warn={warn} />}
        <ChevronDown className={clsx('size-4 text-[var(--muted)] transition-transform duration-200', open ? 'rotate-0' : '-rotate-90')} />
      </button>
      <div id={id} className={clsx('grid transition-[grid-template-rows,opacity] duration-200 ease-out', open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0')}>
        <div className="overflow-hidden">
          <div className="space-y-0.5 pt-0.5 pb-1.5" inert={!open}>
            {items.map((i) => <NavItem key={i.to} item={i} counts={counts} onNavigate={onNavigate} nested />)}
          </div>
        </div>
      </div>
    </div>
  );
}

function ModulePanel({ current, counts, onNavigate }: { current: Module; counts?: NavCounts; onNavigate?: () => void }) {
  const { can, session, switchTenant } = useAuth();
  const loc = useLocation();
  const t = session!.tenant;
  return (
    <div className="flex h-full w-[248px] flex-col border-r border-[var(--border)] bg-[var(--surface)]">
      <div className="px-4 pt-5 pb-3">
        <div className="text-[11px] font-semibold tracking-wider text-[var(--muted)] uppercase">Módulo</div>
        <div className="mt-0.5 text-lg font-bold tracking-tight">{current.label}</div>
        {current.action && can(current.action.perm) && (
          <Link to={current.action.to} onClick={onNavigate} className="mt-3 block">
            <Button className="w-full" icon={<Plus className="size-4" />}>{current.action.label}</Button>
          </Link>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-2.5 pb-4">
        {current.groups.map((g, gi) => {
          const items = g.items.filter((i) => !i.perm || can(i.perm));
          if (!items.length) return null;
          if (!g.title) {
            return <div key={gi} className="mb-1 space-y-0.5">{items.map((i) => <NavItem key={i.to} item={i} counts={counts} onNavigate={onNavigate} />)}</div>;
          }
          return <NavCategory key={`${current.id}-${g.title}`} moduleId={current.id} group={g} items={items} counts={counts} onNavigate={onNavigate} />;
        })}
      </div>
      <div className="border-t border-[var(--border)] p-3">
        <div className="flex items-center gap-2.5 rounded-xl bg-[var(--surface-2)] p-2.5">
          <div className="grid size-8 shrink-0 place-items-center rounded-lg bg-brand-700 text-xs font-bold text-white">{t.legal_name.slice(0, 2).toUpperCase()}</div>
          <div className="min-w-0 flex-1 text-xs">
            <div className="truncate font-semibold">{t.legal_name}</div>
            <div className="truncate text-[var(--muted)]">NUIT {t.nuit} · {t.plan_name}</div>
          </div>
        </div>
        {session!.companies.length > 1 && (
          <select className="mt-2 w-full rounded-lg border border-[var(--border-strong)] bg-[var(--surface)] px-2 py-1.5 text-xs" value={t.id}
            onChange={(e) => switchTenant(e.target.value)} aria-label="Mudar de empresa">
            {session!.companies.map((c) => <option key={c.id} value={c.id}>{c.legal_name}</option>)}
          </select>
        )}
      </div>
    </div>
  );
}

/**
 * "Abrir POS" (docs/09 §5.4): entra no Makila POS com a sessão já iniciada.
 * Só aparece com a integração configurada no servidor e a quem pode abrir
 * (dono, administrador); sem o plano, fica indisponível com a explicação.
 */
function OpenPos() {
  const toast = useToast();
  const st = useQuery({ queryKey: ['pos-status'], queryFn: () => get<{ available: boolean; licensed: boolean; canOpen: boolean }>('/pos/status'), staleTime: 300_000 });
  const [busy, setBusy] = useState(false);
  if (!st.data?.available || !st.data.canOpen) return null;
  if (!st.data.licensed) {
    return (
      <Link to="/subscricao" title="Disponível mediante subscrição" className="hidden items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm text-[var(--muted)] hover:bg-[var(--surface-2)] sm:inline-flex">
        <Store className="size-4" /> Abrir POS <span className="text-xs">· mediante subscrição</span>
      </Link>
    );
  }
  const open = async () => {
    // O separador abre já (no clique), para o navegador não o bloquear; o endereço chega a seguir.
    const w = window.open('about:blank', '_blank');
    if (w) w.opener = null;
    setBusy(true);
    try {
      const { url } = await post<{ url: string }>('/pos/launch');
      if (w) w.location.href = url; else window.location.href = url;
    } catch (e) {
      w?.close();
      toast(errorText(e), 'error');
    } finally { setBusy(false); }
  };
  return <Button variant="ghost" size="sm" icon={<Store className="size-4" />} onClick={open} disabled={busy} aria-label="Abrir o Makila POS"><span className="hidden sm:inline">Abrir POS</span></Button>;
}

export function AppLayout() {
  const { session, loading, can, logout, hostKind, hostSlug, notMemberOfHost } = useAuth();
  const [dark, setDark] = useTheme();
  const [mobile, setMobile] = useState(false);
  const [palette, setPalette] = useState(false);
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem('mk-panel') === '0'; } catch { return false; } });
  const loc = useLocation();
  const nav = useNavigate();
  const current = moduleFor(loc.pathname);
  const counts = useQuery({ queryKey: ['nav-counts'], queryFn: () => get<NavCounts>('/reports/nav-counts'), enabled: !!session, refetchInterval: 60_000 });

  useEffect(() => setMobile(false), [loc.pathname, loc.search]);
  useEffect(() => { try { localStorage.setItem('mk-panel', collapsed ? '0' : '1'); } catch { /* sem storage */ } }, [collapsed]);
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPalette(true); }
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, []);

  if (loading) return <div className="grid h-full place-items-center"><Spinner className="size-7" /></div>;
  if (!session) return <Navigate to="/entrar" replace state={{ from: loc.pathname + loc.search }} />;
  // Autenticado num subdomínio de uma empresa a que a conta não pertence: não
  // mostrar a aplicação de outra empresa aqui; oferecer as empresas desta conta.
  if (notMemberOfHost && hostSlug) {
    const base = window.location.host.replace(new RegExp(`^${hostSlug}\\.`), '');
    return (
      <div className="grid h-full place-items-center p-6">
        <div className="max-w-md space-y-4 rounded-2xl border border-black/5 bg-white p-6 text-center shadow-sm dark:bg-neutral-900">
          <h1 className="text-xl font-bold">Empresa diferente</h1>
          <p className="text-sm text-neutral-500">Esta conta não tem acesso à empresa deste endereço (<b>{hostSlug}</b>). Escolha uma das suas empresas:</p>
          <div className="space-y-2">
            {session.companies.map((co) => (
              <a key={co.id} href={`${window.location.protocol}//${co.slug}.${base}`} className="block rounded-lg border border-black/5 px-4 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/10">{co.legal_name}</a>
            ))}
          </div>
          <button onClick={() => logout()} className="text-sm font-medium text-brand-700 hover:underline">Terminar sessão</button>
        </div>
      </div>
    );
  }
  // Consola de administração (admin.makila.sistemamz.com): só a plataforma vive
  // aqui; tudo o resto encaminha para a visão geral da plataforma.
  if (hostKind === 'admin') {
    const p = loc.pathname;
    if (p === '/' || !(p.startsWith('/plataforma') || p.startsWith('/definicoes'))) {
      return <Navigate to="/plataforma" replace />;
    }
  }
  const t = session.tenant;
  const sub = session.subscription;
  const trialDays = sub.status === 'trial' ? sub.daysLeft : null;
  const c = counts.data;
  const alerts = [
    c?.overdue && { label: `${c.overdue} documento${c.overdue > 1 ? 's' : ''} vencido${c.overdue > 1 ? 's' : ''} por receber`, to: '/contas-correntes', icon: <AlertTriangle className="size-4 text-red-500" /> },
    c?.lowStock && { label: `${c.lowStock} artigo${c.lowStock > 1 ? 's' : ''} abaixo do stock mínimo`, to: '/stock?alerta=1', icon: <Package className="size-4 text-amber-500" /> },
    c?.quotesExpiring && { label: `${c.quotesExpiring} pró-forma${c.quotesExpiring > 1 ? 's' : ''} a caducar nos próximos 3 dias`, to: '/', icon: <Receipt className="size-4 text-amber-500" /> },
    c?.recurringFailed && { label: `${c.recurringFailed} avença${c.recurringFailed > 1 ? 's' : ''} com problema`, to: '/vendas/avencas', icon: <AlertTriangle className="size-4 text-red-500" /> },
    c?.draftsTotal && { label: `${c.draftsTotal} rascunho${c.draftsTotal > 1 ? 's' : ''} por emitir`, to: '/vendas?estado=draft', icon: <FilePlus2 className="size-4 text-amber-500" /> },
  ].filter(Boolean) as { label: string; to: string; icon: React.ReactNode }[];
  const initials = session.user.name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="flex min-h-full">
      {/* Barra dupla — ecrã largo */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden lg:flex">
        <Rail current={current} />
        {!collapsed && <ModulePanel current={current} counts={c} />}
      </aside>
      {/* Gaveta — telemóvel */}
      {mobile && <div className="fixed inset-0 z-40 bg-black/45 lg:hidden" onClick={() => setMobile(false)} />}
      <aside className={clsx('fixed inset-y-0 left-0 z-50 flex shadow-2xl transition-transform lg:hidden', mobile ? 'translate-x-0' : '-translate-x-full')} aria-hidden={!mobile}>
        <Rail current={current} onNavigate={() => setMobile(false)} />
        <ModulePanel current={current} counts={c} onNavigate={() => setMobile(false)} />
        {mobile && <button className="absolute top-3 -right-11 rounded-full bg-white p-2 text-gray-900 shadow" onClick={() => setMobile(false)} aria-label="Fechar menu"><X className="size-5" /></button>}
      </aside>

      <div className={clsx('flex min-w-0 flex-1 flex-col transition-[padding]', collapsed ? 'lg:pl-[76px]' : 'lg:pl-[324px]')}>
        <header className="sticky top-0 z-20 flex h-15 items-center gap-2 border-b border-[var(--border)] bg-[var(--surface)]/85 px-3 backdrop-blur-md sm:px-5">
          <Button variant="ghost" size="sm" iconOnly className="lg:hidden" icon={<Menu className="size-5" />} onClick={() => setMobile(true)} aria-label="Abrir menu" />
          <Button variant="ghost" size="sm" iconOnly className="hidden lg:inline-flex" onClick={() => setCollapsed(!collapsed)}
            icon={collapsed ? <ChevronsRight className="size-4" /> : <ChevronsLeft className="size-4" />} aria-label={collapsed ? 'Mostrar painel do módulo' : 'Esconder painel do módulo'} />
          <button onClick={() => setPalette(true)} aria-label="Pesquisar (Ctrl+K)" className="flex h-9 min-w-0 flex-1 items-center gap-2.5 rounded-lg border border-[var(--border-strong)] bg-[var(--surface-2)] px-3 text-sm text-[var(--muted)] transition hover:border-brand-500/50 sm:max-w-md">
            <Search className="size-4 shrink-0" /><span className="flex-1 truncate text-left"><span className="sm:hidden">Pesquisar…</span><span className="hidden sm:inline">Pesquisar documentos, clientes, artigos…</span></span>
            <kbd className="hidden rounded border border-[var(--border-strong)] bg-[var(--surface)] px-1.5 text-[11px] font-medium sm:inline">Ctrl K</kbd>
          </button>
          <div className="ml-auto flex items-center gap-1.5">
            <OpenPos />
            {can('documents:create') && (
              <Dropdown trigger={<Button size="sm" icon={<Plus className="size-4" />}><span className="hidden sm:inline">Novo</span></Button>} items={[
                { label: 'Factura', icon: <FilePlus2 />, onClick: () => nav('/documentos/novo?tipo=FT') },
                { label: 'Factura-recibo', icon: <Receipt />, onClick: () => nav('/documentos/novo?tipo=FR') },
                { label: 'Venda a dinheiro', icon: <Receipt />, onClick: () => nav('/documentos/novo?tipo=VD') },
                { label: 'Pró-forma', icon: <FilePlus2 />, onClick: () => nav('/documentos/novo?tipo=PF') },
                { label: 'Recibo', icon: <Receipt />, onClick: () => nav('/documentos/novo?tipo=RE') },
                'sep',
                { label: 'Cliente', icon: <UserPlus />, onClick: () => nav('/clientes?novo=1'), hidden: !can('customers:create') },
                { label: 'Artigo', icon: <Package />, onClick: () => nav('/artigos?novo=1'), hidden: !can('products:create') },
                { label: 'Factura de fornecedor', icon: <ShoppingCart />, onClick: () => nav('/compras/nova'), hidden: !can('purchases:create') },
              ]} />
            )}
            <Dropdown trigger={
              <Button variant="ghost" size="sm" iconOnly aria-label={`Alertas (${alerts.length})`} className="relative" icon={<>
                <Bell className="size-[18px]" />
                {alerts.length > 0 && <span className="absolute top-1 right-1 size-2 rounded-full bg-red-500 ring-2 ring-[var(--surface)]" />}
              </>} />
            } items={alerts.length ? alerts.map((a) => ({ label: a.label, icon: a.icon, onClick: () => nav(a.to) })) : [{ label: 'Sem alertas — está tudo em dia', onClick: () => {} }]} />
            <Dropdown trigger={
              <button className="ml-1 grid size-9 place-items-center rounded-full bg-brand-700 text-xs font-bold text-white ring-2 ring-brand-500/20" aria-label="Menu do utilizador">{initials}</button>
            } items={[
              { label: <span><b className="block">{session.user.name}</b><span className="text-xs text-[var(--muted)]">{session.user.email}</span></span>, onClick: () => nav('/definicoes?tab=seguranca') },
              'sep',
              { label: 'Segurança e password', icon: <KeyRound />, onClick: () => nav('/definicoes?tab=seguranca') },
              { label: 'Dados da empresa', icon: <Building2 />, onClick: () => nav('/definicoes?tab=empresa'), hidden: !can('settings:view') },
              { label: 'Consola da plataforma', icon: <Crown />, onClick: () => {
                if (hostKind === 'admin') { nav('/plataforma'); return; }
                const base = hostSlug ? window.location.host.split('.').slice(1).join('.') : window.location.host;
                window.location.href = `${window.location.protocol}//admin.${base}/plataforma`;
              }, hidden: !session.user.is_platform_admin },
              // Guias "Como fazer?" do site público (domínio principal, sem o subdomínio da empresa).
              { label: 'Ajuda — Como fazer?', icon: <LifeBuoy />, onClick: () => {
                const base = hostSlug || hostKind === 'admin' ? window.location.host.split('.').slice(1).join('.') : window.location.host;
                window.open(`${window.location.protocol}//${base}/ajuda`, '_blank', 'noopener');
              } },
              { label: dark ? 'Tema claro' : 'Tema escuro', icon: dark ? <Sun /> : <Moon />, onClick: () => setDark(!dark) },
              'sep',
              { label: 'Sair', icon: <LogOut />, onClick: () => logout(), danger: true },
            ]} />
          </div>
        </header>
        {!sub.writable ? (
          <div role="alert" className="flex flex-wrap items-center justify-center gap-x-2 border-b border-red-600/30 bg-red-600/10 px-4 py-2 text-center text-[13px] text-red-900 dark:text-red-200">
            <Lock className="size-3.5" /><b>Conta em só leitura</b><span>— {sub.reason}</span><span>Pode consultar, imprimir e exportar tudo; não pode criar nem alterar.</span>
            {can('billing:create') && t.status !== 'suspended' && t.status !== 'closed' && <Link to="/subscricao" className="font-semibold underline underline-offset-2">Pagar agora</Link>}
          </div>
        ) : sub.status === 'past_due' ? (
          <div role="status" className="flex flex-wrap items-center justify-center gap-x-2 border-b border-amber-500/25 bg-amber-500/10 px-4 py-2 text-center text-[13px] text-amber-900 dark:text-amber-200">
            <AlertTriangle className="size-3.5" /><b>Pagamento em atraso</b><span>— {sub.reason}</span>
            {can('billing:create') && <Link to="/subscricao" className="font-semibold underline underline-offset-2">Pagar agora</Link>}
          </div>
        ) : t.training_mode ? (
          <div className="flex flex-wrap items-center justify-center gap-x-2 border-b border-amber-500/25 bg-amber-500/10 px-4 py-2 text-center text-[13px] text-amber-900 dark:text-amber-200">
            <b>Modo formação</b><span>— documentos sem valor fiscal, fora da comunicação à AT.</span>
            {trialDays !== null && <span>Teste grátis: {trialDays === 0 ? 'termina hoje' : `${trialDays} dia${trialDays === 1 ? '' : 's'}`}.</span>}
            {can('settings:edit') && <Link to="/definicoes?tab=empresa#emissao" className="font-semibold underline underline-offset-2">Activar emissão fiscal</Link>}
          </div>
        ) : trialDays !== null && trialDays <= 7 ? (
          <div role="status" className="border-b border-amber-500/25 bg-amber-500/10 px-4 py-2 text-center text-[13px] text-amber-900 dark:text-amber-200">
            <b>Teste grátis</b> — {trialDays === 0 ? 'termina hoje' : `termina dentro de ${trialDays} dia${trialDays === 1 ? '' : 's'}`}. Escolha um plano para continuar a facturar.
            {can('billing:create') && <> <Link to="/subscricao" className="font-semibold underline underline-offset-2">Escolher plano</Link></>}
          </div>
        ) : null}
        {!session.user.email_verified_at && <EmailVerifyBanner email={session.user.email} />}
        {session.mfaDeadline && <MfaBanner deadline={session.mfaDeadline} />}
        <AnnouncementsBanner />
        <main className="mx-auto w-full max-w-[1400px] flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8"><Outlet /></main>
      </div>
      <CommandPalette open={palette} onClose={() => setPalette(false)} />
    </div>
  );
}

/**
 * Protecção de rota: sem a permissão, mostra "sem acesso" em vez de montar a
 * página (que faria pedidos à API só para receber 403). A API continua a ser
 * a barreira real; isto é experiência de utilizador.
 */
export function Need({ perm, children }: { perm: Permission; children: ReactNode }) {
  const { can, session } = useAuth();
  if (can(perm)) return <>{children}</>;
  const missing = featureOf(perm).filter((f) => !session?.tenant.features?.[f]);
  if (session && missing.length) {
    return (
      <div className="mx-auto mt-16 max-w-md text-center">
        <div className="mx-auto mb-4 grid size-14 place-items-center rounded-2xl bg-brand-500/10 text-brand-700"><Lock className="size-6" /></div>
        <h1 className="text-xl font-bold">Não incluído no seu plano</h1>
        <p className="mt-2 text-sm text-[var(--muted)]">{missing.map((f) => PLAN_FEATURES[f]).join(' e ')} {missing.length > 1 ? 'fazem' : 'faz'} parte dos planos superiores ao {session.tenant.plan_name}. Contacte-nos para mudar de plano.</p>
        <Link to="/" className="mt-6 inline-block text-sm font-semibold text-brand-700 underline underline-offset-2">Voltar ao início</Link>
      </div>
    );
  }
  if (session && !session.subscription.writable && session.permissions.includes(perm)) {
    return (
      <div className="mx-auto mt-16 max-w-md text-center">
        <div className="mx-auto mb-4 grid size-14 place-items-center rounded-2xl bg-red-600/10 text-red-700"><Lock className="size-6" /></div>
        <h1 className="text-xl font-bold">Conta em só leitura</h1>
        <p className="mt-2 text-sm text-[var(--muted)]">{session.subscription.reason} Pode consultar, imprimir e exportar os documentos existentes.</p>
        <Link to="/" className="mt-6 inline-block text-sm font-semibold text-brand-700 underline underline-offset-2">Voltar ao início</Link>
      </div>
    );
  }
  return (
    <div className="mx-auto mt-16 max-w-md text-center">
      <div className="mx-auto mb-4 grid size-14 place-items-center rounded-2xl bg-brand-500/10 text-brand-700"><Lock className="size-6" /></div>
      <h1 className="text-xl font-bold">Sem acesso a esta área</h1>
      <p className="mt-2 text-sm text-[var(--muted)]">O seu papel ({ROLE_LABELS[session?.role as Role] ?? session?.role}) não inclui esta funcionalidade. Peça ao dono ou administrador da empresa para ajustar as suas permissões.</p>
      <Link to="/" className="mt-6 inline-block text-sm font-semibold text-brand-700 underline underline-offset-2">Voltar ao início</Link>
    </div>
  );
}

/** Dono/administrador sem 2FA: prazo para a activar antes de as acções sensíveis a exigirem. */
function MfaBanner({ deadline }: { deadline: string }) {
  const due = new Date(deadline);
  const late = due.getTime() <= Date.now();
  return (
    <div role="status" className="flex flex-wrap items-center justify-center gap-x-2 border-b border-amber-500/25 bg-amber-500/10 px-4 py-2 text-center text-[13px] text-amber-900 dark:text-amber-200">
      <b>Active a verificação em dois passos</b>
      <span>— {late
        ? 'é obrigatória para donos e administradores: dados bancários, equipa, anulações e exportações estão bloqueados até a activar.'
        : `é obrigatória para donos e administradores a partir de ${due.toLocaleDateString('pt-PT')}.`}</span>
      <Link to="/definicoes?tab=seguranca" className="font-semibold underline underline-offset-2">Activar agora</Link>
    </div>
  );
}

/** Email por confirmar: explica o que fica bloqueado e permite reenviar o endereço. */
function EmailVerifyBanner({ email }: { email: string }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try { const r = await post('/auth/verify-email/resend'); toast(r.status === 'already' ? 'O email já está confirmado.' : `Enviámos um novo endereço para ${email}.`); }
    catch (e) { toast(errorText(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <div role="status" className="flex flex-wrap items-center justify-center gap-x-2 border-b border-blue-500/25 bg-blue-500/10 px-4 py-2 text-center text-[13px] text-blue-900 dark:text-blue-200">
      <b>Confirme o seu email</b><span>— abra o endereço que enviámos para {email}. Até lá não pode enviar documentos por email, convidar pessoas nem activar a emissão fiscal.</span>
      <button type="button" disabled={busy} onClick={resend} className="font-semibold underline underline-offset-2 disabled:opacity-60">Reenviar</button>
    </div>
  );
}
